import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { AppUrlConstants } from '../../../core/constants/AppUrlConstants';

interface TrainingHistoryRow {
  id: number;
  processType: string; // ADD / UPDATE / DELETE
  totalRows: number;
  status: string; // PENDING / IN_PROGRESS / COMPLETED / FAILED / CANCELLED
  createdBy: number | null;
  createdByName: string;
  createdAt: string;
  startedAt: string;
  completedAt: string;
  details: string; // worker summary, or error message if failed
  parentTaskId: number | null; // set when the task was created via Retry
}

interface TrainingPayloadRow {
  id: number;
  question: string;
  answer: string;
  isAbusive: boolean;
  operationType: string;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

@Component({
  selector: 'app-training-history',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './training-history.component.html',
  styleUrl: './training-history.component.css'
})
export class TrainingHistoryComponent implements OnInit, OnDestroy {

  data: TrainingHistoryRow[] = [];
  loading = true;
  page = 1;
  rowsPerPage = 10;
  totalPages = 1;
  totalRows = 0;
  toasts: Toast[] = [];
  skeletonRows = Array.from({ length: 8 });

  // Server-side filters
  statusOptions = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED', 'RETRY'];
  filterStatus = '';
  filterDateFrom = '';
  filterDateTo = '';
  filterUser = '';

  // View Details modal
  showDetailModal = false;
  detailLoading = false;
  detailTask: TrainingHistoryRow | null = null;
  detailPayloads: TrainingPayloadRow[] = [];

  // Cancel confirm modal
  showCancelModal = false;
  cancelTargetRow: TrainingHistoryRow | null = null;

  private pollHandle: any = null;
  private toastIdCounter = 0;

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    this.fetchData();
    // Auto-refresh every 30 seconds (silent — no skeleton flash)
    this.pollHandle = setInterval(() => this.fetchData(true), 30000);
  }

  ngOnDestroy(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
    }
  }

  // ─── Fetch (server-side pagination + filters) ───
  fetchData(silent = false): void {
    if (!silent) {
      this.loading = true;
    }

    let params = new HttpParams()
      .set('page', String(this.page - 1))
      .set('size', String(this.rowsPerPage));
    if (this.filterStatus) params = params.set('status', this.filterStatus);
    if (this.filterDateFrom) params = params.set('dateFrom', this.filterDateFrom);
    if (this.filterDateTo) params = params.set('dateTo', this.filterDateTo);
    const user = this.filterUser.trim();
    if (user && !isNaN(Number(user))) params = params.set('createdBy', user);

    this.http.get<any>(AppUrlConstants.GET_TRAINING_HISTORY, { params }).subscribe({
      next: (json) => {
        const pageDto = json?.data || {};
        this.data = pageDto.data || [];
        this.totalRows = pageDto.totalRows ?? this.data.length;
        this.totalPages = Math.max(1, pageDto.totalPages ?? 1);
        this.loading = false;
      },
      error: () => {
        if (!silent) {
          this.addToast('Failed to load training history', 'error');
          this.data = [];
        }
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.page = 1;
    this.fetchData();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.filterUser = '';
    this.page = 1;
    this.fetchData();
  }

  hasFilters(): boolean {
    return !!(this.filterStatus || this.filterDateFrom || this.filterDateTo || this.filterUser);
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.fetchData();
  }

  onPageSizeChange(): void {
    this.rowsPerPage = Number(this.rowsPerPage);
    this.page = 1;
    this.fetchData();
  }

  // ─── View Details ───
  viewDetails(row: TrainingHistoryRow): void {
    this.detailTask = row;
    this.detailPayloads = [];
    this.detailLoading = true;
    this.showDetailModal = true;

    this.http.get<any>(`${AppUrlConstants.GET_TRAINING_HISTORY}/${row.id}`).subscribe({
      next: (json) => {
        const task = json?.data || {};
        this.detailTask = task;
        this.detailPayloads = task.payloads || [];
        this.detailLoading = false;
      },
      error: (err) => {
        this.detailLoading = false;
        const msg = err?.error?.message || 'Failed to load task details';
        this.addToast(msg, 'error');
      }
    });
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.detailTask = null;
    this.detailPayloads = [];
  }

  // ─── Retry (FAILED only) ───
  retryTask(row: TrainingHistoryRow): void {
    this.http.post<any>(`${AppUrlConstants.GET_TRAINING_HISTORY}/${row.id}/retry`, {}).subscribe({
      next: () => {
        this.addToast('Retry task created successfully');
        this.fetchData(true);
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to retry task';
        this.addToast(msg, 'error');
      }
    });
  }

  // ─── Cancel (PENDING only, with confirm) ───
  confirmCancel(row: TrainingHistoryRow): void {
    this.cancelTargetRow = row;
    this.showCancelModal = true;
  }

  handleCancel(): void {
    this.showCancelModal = false;
    if (!this.cancelTargetRow) return;

    const row = this.cancelTargetRow;
    this.cancelTargetRow = null;

    this.http.post<any>(`${AppUrlConstants.GET_TRAINING_HISTORY}/${row.id}/cancel`, {}).subscribe({
      next: () => {
        this.addToast('Task cancelled successfully');
        this.fetchData(true);
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to cancel task';
        this.addToast(msg, 'error');
        this.fetchData(true);
      }
    });
  }

  // ─── Toast ───
  addToast(message: string, type: 'success' | 'error' = 'success'): void {
    const id = ++this.toastIdCounter;
    this.toasts.push({ id, message, type });
    setTimeout(() => this.removeToast(id), 3000);
  }

  removeToast(id: number): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  // ─── Helpers ───
  getRowClass(idx: number): string {
    return idx % 2 === 0 ? 'row-even' : 'row-odd';
  }

  getStatusClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED') return 'st-completed';
    if (s === 'PENDING') return 'st-pending';
    if (s === 'IN_PROGRESS') return 'st-progress';
    if (s === 'FAILED') return 'st-failed';
    if (s === 'CANCELLED') return 'st-cancelled';
    if (s === 'RETRY') return 'st-retry'; // superseded by a retry task
    return '';
  }
}
